import { faClose } from "@fortawesome/free-solid-svg-icons";
import Icon, { SvgIconName } from "components/components/Icon/Icon";
import { IconSize } from "components/components/Icon/IconWrapper";
import Modal, { ModalSize } from "components/components/Modal/Modal";
import { EventType } from "components/components/Remit/RemitFiltersForm/remitFilters";
import { BoldedText } from "components/homepage/style";
import { EventStatus } from "models/remit/remitMessageDetailsModel";
import React from "react";
import {
  DateFormat,
  formatDateTimeString,
  formatUTCDuration,
  getDurationFromStartAndEndDatesUTC,
} from "utils/dateHelpers";
import routeConstants from "utils/routeConstants";

import {
  ModalHeaderContainer,
  Title,
  InfoTextContainer,
  GridLayoutList,
  GridListItem,
  CloseButton,
  EventStatusCircle,
  MarginLeftTextLink,
} from "./style";

interface Props {
  showModal: boolean;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  mrid: string;
  messageId: number;
  assetId: string;
  eventStatus: EventStatus;
  eventStartTime: Date;
  eventEndTime: Date | null;
  fuelType: string | null;
  normalCapacity: number;
  availableCapacity: number;
  unavailableCapacity: number;
  unavailabilityType: string | null;
  eventType: string | null;
}

const RemitInfoPopup: React.FC<Props> = ({
  showModal,
  setShowModal,
  mrid,
  messageId,
  assetId,
  eventStatus,
  eventStartTime,
  eventEndTime,
  fuelType,
  normalCapacity,
  availableCapacity,
  unavailableCapacity,
  unavailabilityType,
  eventType,
}): JSX.Element => {
  const messageDetailsLink = `${routeConstants.remitMessageDetails}/${mrid}?messageId=${messageId}`;
  return (
    <Modal
      size={ModalSize.medium}
      isActive={showModal}
      onClose={(): void => setShowModal(false)}
    >
      <ModalHeaderContainer>
        <Title>{assetId}</Title>
        <CloseButton
          onClick={(): void => setShowModal(false)}
          data-test-id="close-button"
          aria-label="Close modal"
        >
          <Icon iconName={faClose} size={IconSize.small} />
        </CloseButton>
      </ModalHeaderContainer>
      <GridLayoutList>
        <GridListItem>
          <EventStatusCircle $eventStatus={eventStatus} />
          <InfoTextContainer>
            <BoldedText>Event status </BoldedText>
            <div>{eventStatus}</div>
          </InfoTextContainer>
        </GridListItem>
        {eventEndTime ? (
          <GridListItem>
            <Icon
              iconName={SvgIconName.TimeOutline}
              size={IconSize.mediumSmall}
            />
            <InfoTextContainer>
              <BoldedText>Event duration</BoldedText>
              <div>
                {formatUTCDuration(
                  getDurationFromStartAndEndDatesUTC(
                    eventStartTime,
                    eventEndTime
                  )
                )}
              </div>
            </InfoTextContainer>
          </GridListItem>
        ) : null}
        <GridListItem>
          <Icon
            iconName={SvgIconName.SettingsOutline}
            size={IconSize.mediumSmall}
          />
          <InfoTextContainer>
            <BoldedText>Normal capacity</BoldedText>
            <div>{normalCapacity}</div>
          </InfoTextContainer>
        </GridListItem>
        <GridListItem>
          <Icon
            iconName={SvgIconName.TimeOutline}
            size={IconSize.mediumSmall}
          />
          <InfoTextContainer>
            <BoldedText>Event start</BoldedText>
            <div>
              {formatDateTimeString(eventStartTime, DateFormat.DateTime)}
            </div>
          </InfoTextContainer>
        </GridListItem>
        <GridListItem>
          <Icon
            iconName={SvgIconName.SettingsOutline}
            size={IconSize.mediumSmall}
          />
          <InfoTextContainer>
            <BoldedText>Available capacity</BoldedText>
            <div>{availableCapacity}</div>
          </InfoTextContainer>
        </GridListItem>
        {eventEndTime ? (
          <GridListItem>
            <Icon
              iconName={SvgIconName.TimeOutline}
              size={IconSize.mediumSmall}
            />
            <InfoTextContainer>
              <BoldedText>Event end</BoldedText>
              <div>
                {formatDateTimeString(eventEndTime, DateFormat.DateTime)}
              </div>
            </InfoTextContainer>
          </GridListItem>
        ) : null}
        <GridListItem>
          <Icon
            iconName={SvgIconName.SettingsOutline}
            size={IconSize.mediumSmall}
          />
          <InfoTextContainer>
            <BoldedText>Unavailable capacity </BoldedText>
            <div>{unavailableCapacity}</div>
          </InfoTextContainer>
        </GridListItem>
        {unavailabilityType ? (
          <GridListItem>
            <Icon
              iconName={SvgIconName.CalendarOutline}
              size={IconSize.mediumSmall}
            />
            <InfoTextContainer>
              <BoldedText>Unavailability type</BoldedText>
              <div>{unavailabilityType}</div>
            </InfoTextContainer>
          </GridListItem>
        ) : null}
        {fuelType ? (
          <GridListItem>
            <Icon
              iconName={SvgIconName.InfoOutline}
              size={IconSize.mediumSmall}
            />
            <InfoTextContainer>
              <BoldedText>Fuel type</BoldedText>
              <div>{fuelType}</div>
            </InfoTextContainer>
          </GridListItem>
        ) : null}
        {eventType ? (
          <GridListItem>
            <Icon
              iconName={SvgIconName.InfoOutline}
              size={IconSize.mediumSmall}
            />
            <InfoTextContainer>
              <BoldedText>Event type</BoldedText>
              <div>
                {eventType === EventType.SpaceWeatherEvent
                  ? "Space Weather Event"
                  : eventType}
              </div>
            </InfoTextContainer>
          </GridListItem>
        ) : null}
      </GridLayoutList>
      <MarginLeftTextLink
        href={messageDetailsLink}
        target="_blank"
        className="is-link"
        rel="noreferrer"
      >
        Show full message
      </MarginLeftTextLink>
    </Modal>
  );
};

export default RemitInfoPopup;
